import React from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';
import type { SelectProps } from '@amzn/meridian/select/select';
import { useController } from 'react-hook-form';
import { useTranslation } from 'src/arb-wrapper';
import type { FormRhfControllerProps } from './types';
import Text from '@amzn/meridian/text';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';

export interface FormSelectProps
  extends Omit<
  SelectProps,
  'children' | 'name' | 'value' | 'onBlur' | 'onChange'
  >,
  FormRhfControllerProps {
  /** String values to use as `SelectOption`s */
  options: Array<{ label: string; value: string }>;
  required?: boolean;
  /** Current search query for searchable selects */
  searchQuery?: string;
  /** Callback when user types in searchable select */
  onSearch?: (query: string) => void;
  /** Show loading indicator in dropdown */
  loading?: boolean;
}

/**
 * `FormSelect` is a light wrapper over Meridian `Select`. It adds support for
 * form validation. It also supports passing in an array options instead of
 * constructing options via `SelectOptions` manually via JSX.
 */
export const FormSelect: React.FC<FormSelectProps> = (props) => {
  const { name, control, options, label, required, searchQuery, onSearch, loading, ...meridianProps } = props;
  const { t } = useTranslation();
  const { field, fieldState } = useController({
    control,
    name,
    rules: {
      required: required ? t('form_validation_required_field') : undefined,
    },
  });

  const isMultiple = Array.isArray(field.value);

  return (
    <Select
      {...meridianProps}
      error={!!fieldState.error}
      errorMessage={fieldState.error?.message}
      label={label && required ? `${label}*` : label}
      searchQuery={searchQuery}
      value={field.value}
      onBlur={field.onBlur}
      onChange={(value) => {
        field.onChange(isMultiple && !Array.isArray(value) ? [value] : value);
      }}
      onSearch={onSearch}
    >
      {options.map(({ label, value }) => {
        // @ts-expect-error safe access of testid
        const dataTestIdPrefix = meridianProps['data-testid'];
        const dataTestId = dataTestIdPrefix
          ? `${dataTestIdPrefix}-option-${value}`
          : undefined;

        return (
          <SelectOption
            data-testid={dataTestId}
            key={value}
            label={label}
            value={value}
          />
        );
      })}
      {!options.length
        ? (
        <Column alignmentVertical="center" spacing="300" spacingInset="600">
          {loading
            ? <Loader size="small" />
            : <Text alignment="center">{t('table_no_results')}</Text>
          }
        </Column>
          )
        : null}
    </Select>
  );
};
